import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, Button, Select, Upload, Radio, Tabs, message, Space, App
} from 'antd';
import { UploadOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { matchApi, pdfApi } from '../../services/api';
import './style.css';

const { Option } = Select;
const { TextArea } = Input;

const CreateMatch = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialType = queryParams.get('type') || '题答分离';
  const { message } = App.useApp();

  const [matchType, setMatchType] = useState(initialType);
  const [createMethod, setCreateMethod] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [questionFile, setQuestionFile] = useState(null);
  const [answerFile, setAnswerFile] = useState(null);
  const [integratedFile, setIntegratedFile] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [loadingPdfFiles, setLoadingPdfFiles] = useState(false);

  useEffect(() => {
    // 保存当前科目值
    const currentSubject = form.getFieldValue('subject');
    
    // 重置除科目外的字段
    form.resetFields(['questionFile', 'answerFile', 'questionDoc', 'answerDoc', 
                      'questionText', 'answerText', 'integratedFile', 'integratedDoc', 
                      'integratedText']);
    
    // 如果有科目值，则恢复它
    if (currentSubject) {
      form.setFieldValue('subject', currentSubject);
    }
  }, [matchType, createMethod, form]);

  useEffect(() => {
    // 加载PDF文件列表，用于"选择文档"方式
    const fetchPdfFiles = async () => {
      setLoadingPdfFiles(true);
      try {
        const response = await pdfApi.getFiles();
        setPdfFiles(response);
      } catch (error) {
        console.error('获取PDF文件列表失败', error);
      } finally {
        setLoadingPdfFiles(false);
      }
    };

    if (createMethod === 'select') {
      fetchPdfFiles();
    }
  }, [createMethod]);

  const handleTypeChange = (e) => {
    setMatchType(e.target.value);
  };

  const handleMethodChange = (key) => {
    setCreateMethod(key);
  };

  const beforeUpload = (file) => {
    const isTextOrMd = file.type === 'text/plain' || file.name.endsWith('.md') || file.name.endsWith('.txt');
    if (!isTextOrMd) {
      message.error('只能上传 .txt 或 .md 格式的文件!');
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('文件大小不能超过 5MB!');
    }
    return false;
  };

  const handleQuestionUpload = (info) => {
    setQuestionFile(info.file);
  };

  const handleAnswerUpload = (info) => {
    setAnswerFile(info.file);
  };

  const handleIntegratedUpload = (info) => {
    setIntegratedFile(info.file);
  };

  const uploadFiles = async () => {
    console.log('开始上传文件，匹配类型:', matchType, '创建方式:', createMethod);
    
    if (matchType === '题答分离') {
      if (createMethod === 'upload') {
        if (!questionFile || !answerFile) {
          message.error('请上传题目和答案文件');
          return null;
        }

        try {
          console.log('上传题目文件:', questionFile);
          const questionRes = await matchApi.uploadFile(questionFile, 'question');
          console.log('题目文件上传响应:', questionRes);
          
          console.log('上传答案文件:', answerFile);
          const answerRes = await matchApi.uploadFile(answerFile, 'answer');
          console.log('答案文件上传响应:', answerRes);
          
          return {
            question_filename: questionRes.filename,
            question_content: questionRes.content,
            answer_filename: answerRes.filename,
            answer_content: answerRes.content
          };
        } catch (error) {
          console.error('文件上传失败:', error);
          if (error.response) {
            console.error('上传错误响应:', error.response.data);
            console.error('状态码:', error.response.status);
          }
          message.error('文件上传失败');
          return null;
        }
      } else if (createMethod === 'select') {
        const values = form.getFieldsValue();
        if (!values.questionDoc || !values.answerDoc) {
          message.error('请选择题目和答案文档');
          return null;
        }

        try {
          const questionFile = pdfFiles.items?.find(file => file.id === values.questionDoc);
          const answerFile = pdfFiles.items?.find(file => file.id === values.answerDoc);
          
          if (!questionFile || !answerFile) {
            message.error('找不到选择的文档');
            return null;
          }
          
          return {
            question_filename: questionFile.filename,
            question_content: questionFile.extract_result || '',
            answer_filename: answerFile.filename,
            answer_content: answerFile.extract_result || '',
            question_file_id: questionFile.id,
            answer_file_id: answerFile.id
          };
        } catch (error) {
          message.error('获取文档内容失败');
          console.error(error);
          return null;
        }
      } else if (createMethod === 'text') {
        const values = form.getFieldsValue();
        if (!values.questionText || !values.answerText) {
          message.error('请输入题目和答案文本');
          return null;
        }
        
        return {
          question_filename: 'pasted_question.txt',
          question_content: values.questionText,
          answer_filename: 'pasted_answer.txt',
          answer_content: values.answerText
        };
      }
    } else { // 题答一体
      if (createMethod === 'upload') {
        if (!integratedFile) {
          message.error('请上传题目答案文件');
          return null;
        }

        try {
          const fileRes = await matchApi.uploadFile(integratedFile, 'integrated');
          
          return {
            question_filename: fileRes.filename,
            question_content: fileRes.content,
            answer_filename: null,
            answer_content: null
          };
        } catch (error) {
          message.error('文件上传失败');
          console.error(error);
          return null;
        }
      } else if (createMethod === 'select') {
        const values = form.getFieldsValue();
        if (!values.integratedDoc) {
          message.error('请选择题目答案文档');
          return null;
        }

        try {
          const file = pdfFiles.items?.find(file => file.id === values.integratedDoc);
          
          if (!file) {
            message.error('找不到选择的文档');
            return null;
          }
          
          return {
            question_filename: file.filename,
            question_content: file.extract_result || '',
            answer_filename: null,
            answer_content: null,
            question_file_id: file.id
          };
        } catch (error) {
          message.error('获取文档内容失败');
          console.error(error);
          return null;
        }
      } else if (createMethod === 'text') {
        const values = form.getFieldsValue();
        if (!values.integratedText) {
          message.error('请输入题目答案文本');
          return null;
        }
        
        return {
          question_filename: 'pasted_integrated.txt',
          question_content: values.integratedText,
          answer_filename: null,
          answer_content: null
        };
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {
    try {
      // 只验证当前创建方式相关的字段
      const fieldsToValidate = ['subject'];
      
      console.log('开始提交，匹配类型:', matchType, '创建方式:', createMethod);
      
      if (matchType === '题答分离') {
        if (createMethod === 'upload') {
          fieldsToValidate.push('questionFile', 'answerFile');
        } else if (createMethod === 'select') {
          fieldsToValidate.push('questionDoc', 'answerDoc');
        } else if (createMethod === 'text') {
          fieldsToValidate.push('questionText', 'answerText');
        }
      } else { // 题答一体
        if (createMethod === 'upload') {
          fieldsToValidate.push('integratedFile');
        } else if (createMethod === 'select') {
          fieldsToValidate.push('integratedDoc');
        } else if (createMethod === 'text') {
          fieldsToValidate.push('integratedText');
        }
      }
      
      console.log('待验证字段:', fieldsToValidate);
      await form.validateFields(fieldsToValidate);
      
      setLoading(true);
      
      const values = form.getFieldsValue();
      console.log('表单数据:', values);
      
      const fileData = await uploadFiles();
      console.log('文件上传结果:', fileData);
      
      if (!fileData) {
        setLoading(false);
        return;
      }
      
      const recordData = {
        subject: values.subject,
        match_type: matchType === '题答分离' ? '题答分离' : '题答一体',
        ...fileData
      };
      
      console.log('准备提交的记录数据:', recordData);
      const response = await matchApi.createRecord(recordData);
      console.log('创建记录响应:', response);
      
      message.success('创建成功');
      navigate(`/match/edit/${response.id}`);
    } catch (error) {
      console.error('表单提交错误:', error);
      
      if (error.errorFields) {
        // 表单验证错误
        const errorMessages = error.errorFields.map(field => field.errors[0]).join(', ');
        console.error('表单验证错误详情:', error.errorFields);
        message.error(`表单验证失败: ${errorMessages}`);
      } else if (error.response) {
        // API 请求错误
        console.error('API错误响应:', error.response.data);
        console.error('状态码:', error.response.status);
        message.error(`提交失败: ${error.response.data?.detail || '服务器错误'}`);
      } else {
        // 其他错误
        message.error('提交失败: ' + (error.message || '未知错误'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSeparateContent = () => {
    const items = [
      {
        key: 'upload',
        label: <span><UploadOutlined />上传文件</span>,
        children: (
          <>
            <Form.Item 
              label="题目文件" 
              name="questionFile" 
              rules={[
                { 
                  required: createMethod === 'upload', 
                  message: '请上传题目文件' 
                }
              ]}
              valuePropName="fileList"
              getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
            >
              <Upload 
                beforeUpload={beforeUpload} 
                onChange={handleQuestionUpload}
                maxCount={1}
                showUploadList={true}
              >
                <Button icon={<UploadOutlined />}>上传题目文件</Button>
              </Upload>
            </Form.Item>
            <Form.Item 
              label="答案文件" 
              name="answerFile" 
              rules={[
                { 
                  required: createMethod === 'upload', 
                  message: '请上传答案文件' 
                }
              ]}
              valuePropName="fileList"
              getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
            >
              <Upload 
                beforeUpload={beforeUpload} 
                onChange={handleAnswerUpload}
                maxCount={1}
                showUploadList={true}
              >
                <Button icon={<UploadOutlined />}>上传答案文件</Button>
              </Upload>
            </Form.Item>
          </>
        ),
      },
      {
        key: 'select',
        label: <span><FileTextOutlined />选择文档</span>,
        children: (
          <>
            <Form.Item 
              label="题目文档" 
              name="questionDoc" 
              rules={[
                { 
                  required: createMethod === 'select', 
                  message: '请选择题目文档' 
                }
              ]}
            >
              <Select placeholder="选择题目文档" loading={loadingPdfFiles}>
                {pdfFiles.items?.filter(file => file.content_type === '题目').map(file => (
                  <Option key={file.id} value={file.id}>{file.filename}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item 
              label="答案文档" 
              name="answerDoc" 
              rules={[
                { 
                  required: createMethod === 'select', 
                  message: '请选择答案文档' 
                }
              ]}
            >
              <Select placeholder="选择答案文档" loading={loadingPdfFiles}>
                {pdfFiles.items?.filter(file => file.content_type === '答案').map(file => (
                  <Option key={file.id} value={file.id}>{file.filename}</Option>
                ))}
              </Select>
            </Form.Item>
          </>
        ),
      },
      {
        key: 'text',
        label: <span><EditOutlined />粘贴文本</span>,
        children: (
          <>
            <Form.Item 
              label="题目文本" 
              name="questionText" 
              rules={[
                { 
                  required: createMethod === 'text', 
                  message: '请输入题目文本' 
                }
              ]}
            >
              <TextArea rows={10} placeholder="请粘贴题目文本" />
            </Form.Item>
            <Form.Item 
              label="答案文本" 
              name="answerText" 
              rules={[
                { 
                  required: createMethod === 'text', 
                  message: '请输入答案文本' 
                }
              ]}
            >
              <TextArea rows={10} placeholder="请粘贴答案文本" />
            </Form.Item>
          </>
        ),
      },
    ];

    return (
      <Tabs activeKey={createMethod} onChange={handleMethodChange} items={items} />
    );
  };

  const renderIntegratedContent = () => {
    const items = [
      {
        key: 'upload',
        label: <span><UploadOutlined />上传文件</span>,
        children: (
          <Form.Item 
            label="题目答案文件" 
            name="integratedFile" 
            rules={[
              { 
                required: createMethod === 'upload', 
                message: '请上传题目答案文件' 
              }
            ]}
            valuePropName="fileList"
            getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
          >
            <Upload 
              beforeUpload={beforeUpload} 
              onChange={handleIntegratedUpload}
              maxCount={1}
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>上传题目答案文件</Button>
            </Upload>
          </Form.Item>
        ),
      },
      {
        key: 'select',
        label: <span><FileTextOutlined />选择文档</span>,
        children: (
          <Form.Item 
            label="题目答案文档" 
            name="integratedDoc" 
            rules={[
              { 
                required: createMethod === 'select', 
                message: '请选择题目答案文档' 
              }
            ]}
          >
            <Select placeholder="选择题目答案文档" loading={loadingPdfFiles}>
              {pdfFiles.items?.filter(file => file.content_type === '题目和答案').map(file => (
                <Option key={file.id} value={file.id}>{file.filename}</Option>
              ))}
            </Select>
          </Form.Item>
        ),
      },
      {
        key: 'text',
        label: <span><EditOutlined />粘贴文本</span>,
        children: (
          <Form.Item 
            label="题目答案文本" 
            name="integratedText" 
            rules={[
              { 
                required: createMethod === 'text', 
                message: '请输入题目答案文本' 
              }
            ]}
          >
            <TextArea rows={15} placeholder="请粘贴题目答案文本" />
          </Form.Item>
        ),
      },
    ];

    return (
      <Tabs activeKey={createMethod} onChange={handleMethodChange} items={items} />
    );
  };

  return (
    <div className="create-match-container">
      <Card title="创建匹配项目">
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item label="匹配模式" name="matchType" initialValue={matchType}>
            <Radio.Group onChange={handleTypeChange} value={matchType}>
              <Radio.Button value="题答分离">题答分离匹配</Radio.Button>
              <Radio.Button value="题答一体">题答一体匹配</Radio.Button>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item label="科目" name="subject" rules={[{ required: true, message: '请选择科目' }]}>
            <Select 
              placeholder="选择科目"
              getPopupContainer={(trigger) => trigger.parentNode}
              popupMatchSelectWidth={false}
              listHeight={256}
              placement="bottomLeft"
            >
              <Option value="大学语文">大学语文</Option>
              <Option value="高等数学">高等数学</Option>
              <Option value="计算机等级考试">计算机等级考试</Option>
              <Option value="英语四级等级考试">英语四级等级考试</Option>
              <Option value="日语四级等级考试">日语四级等级考试</Option>
              <Option value="财经类">财经类</Option>
              <Option value="管理类">管理类</Option>
              <Option value="电子信息类">电子信息类</Option>
              <Option value="计算机类">计算机类</Option>
              <Option value="机械工程类">机械工程类</Option>
              <Option value="音乐类">音乐类</Option>
              <Option value="美术设计类">美术设计类</Option>
              <Option value="化工生物类">化工生物类</Option>
              <Option value="文史类">文史类</Option>
              <Option value="土木建筑类">土木建筑类</Option>
              <Option value="新闻传播类">新闻传播类</Option>
              <Option value="医护类">医护类</Option>
              <Option value="日语类">日语类</Option>
              <Option value="英语类">英语类</Option>
              <Option value="法学类">法学类</Option>
              <Option value="教育类">教育类</Option>
              <Option value="资源环境类">资源环境类</Option>
              <Option value="农林类">农林类</Option>
              <Option value="食品类">食品类</Option>
            </Select>
          </Form.Item>
          
          {matchType === '题答分离' ? renderSeparateContent() : renderIntegratedContent()}
          
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                开始整理
              </Button>
              <Button onClick={() => navigate('/match')}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateMatch;